<template>
  <div class="voyage-stop"
       :class="{ 
         active: isActive,
         'mobile-view': isMobile 
       }"
       :data-location="locationKey"
       ref="voyageStop">
    <div class="stop-header">
      <h2 class="text-2xl font-bold mb-2">{{ formattedTitle }}</h2>
      <p class="text-gray-600 mb-1">{{ date }}</p>
    </div>
    
    <div class="stop-content">
      <p v-html="description"></p>
      <div class="photos-container">
        <div v-for="(photo, index) in photos" 
           :key="index" 
           class="photo-placeholder"
           :style="getPhotoStyle(photo)"
           @click="openPhoto(photo)">
        <img v-if="photo"
             :src="photo"
             :alt="`Photo ${index + 1}`"
             class="hidden"
             @load="handlePhotoLoad(index)"
             @error="handlePhotoError(index)"/>
        <span v-if="!photo || photoErrors[index]">Add Photo {{ index + 1 }}</span>
      </div>
      </div>
    </div>
    
    <PhotoModal 
      :show="!!selectedPhoto"
      :photo-url="selectedPhoto"
      :alt="'Selected photo'"
      @close="closePhoto"
    />
  </div>
</template>

<script>
import { computed, onMounted, ref, watch, onBeforeUnmount } from 'vue';
import PhotoModal from './PhotoModal.vue';

export default {
  name: 'VoyageStop',
  components: {
    PhotoModal
  },
  props: {
    locationKey: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    date: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    photoCount: {
      type: Number,
      default: 1
    },
    isActive: {
      type: Boolean,
      default: false
    },
    photos: {
      type: Array,
      default: () => []
    },
    isMinimized: {
      type: Boolean,
      default: false
    }
  },

  setup(props) {
    // Add immediate debugging for props
    console.log('VoyageStop props:', {
      locationKey: props.locationKey,
      photoCount: props.photoCount,
      photos: props.photos
    });

    const photoErrors = ref({});
    const loadedPhotos = ref({});
    const selectedPhoto = ref(null);
    const isMobile = ref(window.innerWidth <= 768);
    
    const handleCardClick = () => {
      if (isMobile.value) {
        isLocalMinimized.value = false;
      }
    };

    onMounted(() => {
      window.addEventListener('resize', () => {
        isMobile.value = window.innerWidth <= 768;
      });
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', () => {
        isMobile.value = window.innerWidth <= 768;
      });
    });

    // Watch photos prop for changes
    watch(() => props.photos, (newPhotos) => {
      console.log('Photos updated:', newPhotos);
      photoErrors.value = {};
      loadedPhotos.value = {};
    }, { immediate: true });

    const formattedTitle = computed(() => {
      return props.title.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    });

    const getPhotoStyle = (photo) => {
      if (!photo || photoErrors.value[props.photos.indexOf(photo)]) {
        console.log('No photo or error for:', photo);
        return {
          background: 'linear-gradient(135deg, #f7fafc, #edf2f7)'
        };
      }
      
      if (loadedPhotos.value[props.photos.indexOf(photo)]) {
        const photoUrl = new URL(photo, window.location.origin).href;
        console.log('Loading photo:', photoUrl);
        return {
          backgroundImage: `url('${photoUrl}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          border: 'none'
        };
      }

      return {
        background: 'linear-gradient(135deg, #f7fafc, #edf2f7)'
      };
    };

    const handlePhotoLoad = (index) => {
      console.log(`Photo ${index} loaded successfully`);
      loadedPhotos.value[index] = true;
      photoErrors.value[index] = false;
    };

    const handlePhotoError = (index) => {
      console.error(`Failed to load photo at index ${index}`);
      photoErrors.value[index] = true;
      loadedPhotos.value[index] = false;
    };

    const openPhoto = (photo) => {
      if (photo && !photoErrors.value[props.photos.indexOf(photo)]) {
        selectedPhoto.value = photo;
      }
    };

    const closePhoto = () => {
      selectedPhoto.value = null;
    };

    onMounted(() => {
      console.log('VoyageStop mounted:', {
        locationKey: props.locationKey,
        photos: props.photos,
        photoUrls: props.photos?.map(photo => new URL(photo, window.location.origin).href)
      });
      console.log('Initial photos:', props.photos);
      props.photos.forEach((photo, index) => {
        console.log(`Photo ${index} path:`, new URL(photo, window.location.origin).href);
      });
      // Ensure data-location attribute is set
      if (props.locationKey) {
        const element = document.querySelector(`[data-location="${props.locationKey}"]`);
        if (element) {
          element.setAttribute('data-location', props.locationKey);
        }
      }
    });

    return {
      formattedTitle,
      getPhotoStyle,
      handlePhotoLoad,
      handlePhotoError,
      openPhoto,
      closePhoto,
      photoErrors,
      loadedPhotos,
      selectedPhoto,
      isMobile,
      handleCardClick
    };
  }
};
</script>

<style scoped>
.voyage-stop {
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.92));
  border: none;
  border-radius: clamp(0.75rem, 3vw, 1.25rem);
  box-shadow: 
    0 8px 16px rgba(0, 0, 0, 0.1),
    0 2px 4px rgba(0, 0, 0, 0.08),
    inset 0 0 0 1px rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  opacity: 0.95;
  overflow: visible;
  margin-bottom: clamp(20vh, 30vh, 40vh);
  padding: clamp(1.5rem, 5vw, 2.5rem);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateY(0);
  scroll-margin-top: calc(clamp(120px, 15vh, 150px) + 2rem);
  min-height: clamp(30vh, 35vh, 40vh);
  position: relative;
}

.voyage-stop::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 8px;
  background: linear-gradient(90deg, #3B82F6, #2563EB);
  border-radius: 1.25rem 1.25rem 0 0;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.voyage-stop.active {
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.99), rgba(255, 255, 255, 0.96));
  transform: translateY(-6px);
  box-shadow: 
    0 20px 25px rgba(0, 0, 0, 0.1),
    0 10px 10px rgba(0, 0, 0, 0.04),
    inset 0 0 0 1px rgba(255, 255, 255, 0.9);
  opacity: 1;
}

.voyage-stop.active::before {
  opacity: 1;
}

.voyage-stop h2 {
  color: #1a365d;
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  margin-bottom: 1rem;
  line-height: 1.2;
  background: linear-gradient(135deg, #1a365d, #2563EB);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.voyage-stop .text-gray-600 {
  color: #4a5568;
  font-size: clamp(0.75rem, 2vw, 0.875rem);
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: clamp(1rem, 3vw, 1.5rem);
  opacity: 0.9;
  position: relative;
  padding-left: 1rem;
}

.voyage-stop .text-gray-600::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 4px;
  height: 4px;
  background: #3B82F6;
  border-radius: 50%;
  transform: translateY(-50%);
}

.voyage-stop p:not(.text-gray-600) {
  color: #2d3748;
  line-height: 1.6;
  margin-bottom: clamp(1.5rem, 4vw, 2rem);
  font-size: clamp(1rem, 2.5vw, 1.125rem);
  font-weight: 400;
}

.photos-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.photo-placeholder {
  aspect-ratio: 1;
  background: linear-gradient(135deg, #f7fafc, #edf2f7);
  border: 2px dashed #cbd5e0;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #4a5568;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
  min-height: 160px;
  min-width: 160px;
  background-position: center center !important;
  background-repeat: no-repeat !important;
  background-size: cover !important;
  cursor: pointer;
}

.photo-placeholder[style*="background-image"] {
  border: none;
  position: relative;
  overflow: hidden;
}

.photo-placeholder[style*="background-image"]::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: inherit;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.photo-placeholder[style*="background-image"]:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.photo-placeholder:not([style*="background-image"]):hover {
  border-color: #3B82F6;
  background: linear-gradient(135deg, #ebf8ff, #e6fffa);
}

.photo-placeholder:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.photo-placeholder[style*="background-image"]:hover::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
  transition: background 0.3s ease;
}

.hidden {
  display: none;
}

.mobile-view {
  position: relative;
}

@media (max-width: 480px) {
  .voyage-stop {
    margin-bottom: 25vh;
    scroll-margin-top: 25vh;
  }

  .photos-container {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.35rem;
  }

  .photo-placeholder {
    aspect-ratio: 1;
    font-size: 0.75rem;
    min-height: 85px;
    min-width: 85px;
  }
}

@media (max-width: 360px) {
  .photos-container {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.25rem;
  }

  .photo-placeholder {
    min-height: 70px;
    min-width: 70px;
  }
}

/* Mobile Styles */
@media (max-width: 768px) {
  .voyage-stop {
    padding: 1rem;
    margin-bottom: 1rem;
  }

  .stop-header {
    margin-bottom: 0.75rem;
  }

  .stop-content {
    margin-top: 0.5rem;
  }

  .photos-container {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
    margin-top: 1rem;
    padding-top: 1rem;
  }

  .photo-placeholder {
    min-height: 100px;
    min-width: 100px;
  }
}

@media (max-width: 480px) {
  .preview-photo {
    width: 50px;
    height: 50px;
  }

  .preview-text {
    font-size: 0.8125rem;
  }
}
</style>
